import { decodeBase64, decodeUTF8 } from 'tweetnacl-util'
import nacl from 'tweetnacl'
import jwtDefaultConfig from './jwtDefaultConfig'
import store from '@/store'

export default class JwtService {
  // Will be used by this service for making API calls
  axiosIns = null

  // jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig }

  // For Refreshing Token
  refreshPromise = null

  // For Refreshing Token
  subscribers = []

  constructor(axiosIns, jwtOverrideConfig) {
    this.axiosIns = axiosIns
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

    // Request Interceptor
    this.axiosIns.interceptors.request.use(
      config => {
        // Get token from localStorage
        const accessToken = this.getToken()

        // If token is present add it to request's Authorization Header
        if (accessToken && !config.skipAuth) {
          // eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
        }
        return config
      },
      error => Promise.reject(error),
    )

    // Add request/response interceptor
    this.axiosIns.interceptors.response.use(
      response => response,
      error => {
        const { config, response } = error
        if (response && response.status === 401 && config && !config.skipAuth && !config.retriedAfterRefresh && this.getRefreshToken()) {
          // All concurrent failures wait for the same refresh request.
          if (!this.refreshPromise) {
            this.refreshPromise = this.refreshToken()
              .then(({ data }) => {
                this.setToken(data.access)
                if (data.refresh) this.setRefreshToken(data.refresh)
                return data.access
              })
              .catch(refreshError => {
                this.clearSession()
                throw refreshError
              })
              .finally(() => {
                this.refreshPromise = null
              })
          }
          return this.refreshPromise.then(accessToken => {
            config.retriedAfterRefresh = true
            config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
            return this.axiosIns(config)
          })
        }
        return Promise.reject(error)
      },
    )
  }

  fetchUserData() {
    return new Promise((resolve, reject) => {
      this.axiosIns.get(this.jwtConfig.userDataEndpoint).then(response => {
        this.setUserData(response.data)
        resolve(response)
      }).catch(error => {
        reject(error)
      })
    })
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers = this.subscribers.filter(callback => callback(accessToken))
  }

  addSubscriber(callback) {
    this.subscribers.push(callback)
  }

  getToken() {
    return localStorage.getItem(this.jwtConfig.storageTokenKeyName)
  }

  getRefreshToken() {
    return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
  }

  setToken(value) {
    localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
  }

  setUserData(value) {
    store.commit('auth/SET_USER', value)
    store.commit('auth/SET_LOADED', true)
  }

  setRefreshToken(value) {
    localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)
  }

  login(body) {
    return this.axiosIns.post(this.jwtConfig.loginEndpoint, body, { skipAuth: true })
  }

  logout() {
    const refreshToken = this.getRefreshToken()
    const accessToken = this.getToken()
    const blacklistPromise = this.blacklistToken(refreshToken, accessToken).catch(() => {})

    this.clearSession()

    return blacklistPromise
  }

  blacklistToken(refreshToken = this.getRefreshToken(), accessToken = this.getToken()) {
    if (!refreshToken) {
      return Promise.resolve()
    }

    const config = accessToken
      ? { headers: { Authorization: `${this.jwtConfig.tokenType} ${accessToken}` } }
      : undefined

    return this.axiosIns.post(this.jwtConfig.logoutEndpoint, { refresh: refreshToken }, config)
  }

  refreshToken() {
    return this.axiosIns.post(this.jwtConfig.refreshEndpoint, {
      refresh: this.getRefreshToken(),
    }, { skipAuth: true })
  }

  forgotPassword(email) {
    return this.axiosIns.get(this.jwtConfig.forgotPasswordEndpoint, {
      params: {
        email,
      },
    })
  }

  resetPassword(body) {
    return this.axiosIns.post(this.jwtConfig.resetPasswordEndpoint, body)
  }

  clearSession() {
    localStorage.removeItem(this.jwtConfig.storageTokenKeyName)
    localStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName)
    localStorage.removeItem(this.jwtConfig.storageUserDataKeyName)

    // Remove userData from localStorage
    store.dispatch('auth/clear')
  }

  setAll(responseData) {
    this.setToken(responseData[this.jwtConfig.storageTokenKeyName])
    this.setRefreshToken(responseData[this.jwtConfig.storageRefreshTokenKeyName])
    this.onAccessTokenFetched(responseData[this.jwtConfig.storageTokenKeyName])
    return this.fetchUserData()
  }

  static deepCanonicalize(value) {
    if (Array.isArray(value)) {
      return value.map(v => JwtService.deepCanonicalize(v))
    }
    if (value && typeof value === 'object') {
      const out = {}
      for (const k of Object.keys(value).sort()) {
        out[k] = JwtService.deepCanonicalize(value[k])
      }
      return out
    }
    return value
  }

  static stableStringify(obj) {
    return JSON.stringify(JwtService.deepCanonicalize(obj))
  }

  static verifyMeResponse({ payload, sig }) {
    const msg = decodeUTF8(JwtService.stableStringify(payload))
    const signature = decodeBase64(sig)
    const pub = decodeBase64(process.env.VUE_APP_ME_PUBKEY_B64)
    return nacl.sign.detached.verify(msg, signature, pub)
  }
}
